/**
 * Instanced tile shader (03 §6, tiles.json `sampling`). Tiles live as layers of RGBA8 texture
 * arrays (one array "page" = one draw); each instance is one tile quad with
 * world0 = (x0, y0, tile_m, layer). Texel row 0 = south edge, values are raw data (texel / 255).
 *
 * The style's grade hook supplies `vec4 ucGrade(vec2 uv, vec2 world)`, built from:
 *
 *   ucTexelNearest(uv)            raw texel containing uv (clamped to the tile)
 *   ucTexelFiltered(uv, weighted) A = plain bilinear; rgb channels with weighted = 1 are
 *                                 coverage-weighted bilinear (Σ w·A·X / Σ w·A, 0 when Σ w·A = 0),
 *                                 channels with 0 are nearest. Taps clamp inside the same tile.
 *   ucStageCell(), ucBayer4(cell), ucStagePx()   stage-px grid helpers (common.ts)
 *
 * Sampling modes (03 §10 gap 5): the arrays behind `uTiles` are NEAREST + texelFetch with no mip
 * chain unless the layer opts into `sampling: 'linear'` (`tileSamplingGlsl(true)` swaps the
 * sampling functions and the linear cache builds mipmapped TRILINEAR pages). The LINEAR branch is
 * one hardware tap = bilinear + trilinear mip filtering on the straight RGBA (the `day` colour
 * contract, `src/web_export/tiles_day.py`); the alpha comes back filtered, so hooks keep their
 * `s.a <= 0.5` coverage test. Per-channel weighted/nearest mixing is impossible in one tap, so
 * `weighted` is inert there and rgb is plain-filtered (at coverage edges it mixes the zero rgb of
 * A = 0 texels one texel wide — plan §10 D-1, accepted). The NEAREST source (`GLSL_TILE_SAMPLING`)
 * is byte-identical to the pre-M5 shader, so night/graphite layers compile the same program
 * (enforced by gl-tile-linear.test.ts).
 */
import { GLSL_HUB, GLSL_STAGE_FRAG } from './common.ts';

export const TILE_VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
#ifdef INSTANCES
attribute vec4 world0;
attribute vec4 world1;
attribute vec4 world2;
attribute vec4 world3;
#endif
uniform mat4 viewProjection;
uniform float uMinTileM;
varying vec2 vWorld;
flat varying vec4 vTile;
void main() {
#ifdef INSTANCES
  vec4 t = world0;
#else
  vec4 t = vec4(0.0, 0.0, 1.0, 0.0);
#endif
  vec2 w = t.xy + position.xz * t.z;
  // coarser levels (parent fallback) sit slightly lower: finer tiles win the depth test
  float y = -0.01 * log2(t.z / uMinTileM);
  vWorld = w;
  vTile = t;
  gl_Position = viewProjection * vec4(w.x, y, w.y, 1.0);
}
`;

// Shared declarations (both sampling modes). Interpolated verbatim, so the assembled NEAREST
// string is byte-identical to the pre-M5 GLSL_TILE_SAMPLING.
const GLSL_TILE_HEADER = /* glsl */ `
precision highp sampler2DArray;
uniform sampler2DArray uTiles;
// set by main() before ucGrade: the tile's array layer and (x0, y0, tile_m)
int ucLayer;
vec3 ucTileRect;
`;

/** NEAREST pages: texelFetch, no mip chain (Esposizione night, Strategia graphite — unchanged). */
export const GLSL_TILE_SAMPLING = /* glsl */ `${GLSL_TILE_HEADER}vec4 ucTexelNearest(vec2 uv) {
  ivec2 n = textureSize(uTiles, 0).xy;
  ivec2 ij = clamp(ivec2(floor(uv * vec2(n))), ivec2(0), n - 1);
  return texelFetch(uTiles, ivec3(ij, ucLayer), 0);
}
vec4 ucTexelFiltered(vec2 uv, vec3 weighted) {
  ivec2 n = textureSize(uTiles, 0).xy;
  ivec2 hi = n - 1;
  vec2 t = uv * vec2(n) - 0.5;
  vec2 i0f = floor(t);
  vec2 f = t - i0f;
  ivec2 i0 = ivec2(i0f);
  vec4 s00 = texelFetch(uTiles, ivec3(clamp(i0, ivec2(0), hi), ucLayer), 0);
  vec4 s10 = texelFetch(uTiles, ivec3(clamp(i0 + ivec2(1, 0), ivec2(0), hi), ucLayer), 0);
  vec4 s01 = texelFetch(uTiles, ivec3(clamp(i0 + ivec2(0, 1), ivec2(0), hi), ucLayer), 0);
  vec4 s11 = texelFetch(uTiles, ivec3(clamp(i0 + ivec2(1, 1), ivec2(0), hi), ucLayer), 0);
  float w00 = (1.0 - f.x) * (1.0 - f.y);
  float w10 = f.x * (1.0 - f.y);
  float w01 = (1.0 - f.x) * f.y;
  float w11 = f.x * f.y;
  float a = w00 * s00.a + w10 * s10.a + w01 * s01.a + w11 * s11.a;
  vec3 num = w00 * s00.a * s00.rgb + w10 * s10.a * s10.rgb + w01 * s01.a * s01.rgb + w11 * s11.a * s11.rgb;
  vec3 lin = a > 0.0 ? num / a : vec3(0.0);
  vec3 nea = ucTexelNearest(uv).rgb;
  return vec4(mix(nea, lin, weighted), a);
}
`;

/** LINEAR pages (03 §10 gap 5): mipmapped arrays + TRILINEAR samplers; one hardware tap = bilinear
 *  + trilinear mip filtering on the straight RGBA, filtered coverage in .a (hooks keep the
 *  `s.a <= 0.5` test). `weighted` is inert: per-channel weighted/nearest mixing is impossible in
 *  one tap; at coverage edges rgb mixes the zero rgb of A = 0 texels one texel wide (the `day`
 *  contract, tiles_day.py; plan §10 D-1). ucTexelNearest stays texelFetch for raw probes. */
export const GLSL_TILE_SAMPLING_LINEAR = /* glsl */ `${GLSL_TILE_HEADER}vec4 ucTexelNearest(vec2 uv) {
  ivec2 n = textureSize(uTiles, 0).xy;
  ivec2 ij = clamp(ivec2(floor(uv * vec2(n))), ivec2(0), n - 1);
  return texelFetch(uTiles, ivec3(ij, ucLayer), 0);
}
vec4 ucTexelFiltered(vec2 uv, vec3 weighted) {
  return texture(uTiles, vec3(uv, float(ucLayer)));
}
`;

/** The sampling functions for a layer's sampling mode. */
export function tileSamplingGlsl(linear: boolean): string {
  return linear ? GLSL_TILE_SAMPLING_LINEAR : GLSL_TILE_SAMPLING;
}

/** Build the tile fragment shader around a grade hook's GLSL. `linear` selects the hardware-filtered
 *  mip path (the pages must come from a `sampling: 'linear'` cache). Without it the output is
 *  byte-identical to the pre-M5 shader. */
export function tileFragment(gradeGlsl: string, linear = false): string {
  return /* glsl */ `
precision highp float;
precision highp int;
${GLSL_HUB}
${GLSL_STAGE_FRAG}
${tileSamplingGlsl(linear)}
varying vec2 vWorld;
flat varying vec4 vTile;
${gradeGlsl}
void main() {
  ucLayer = int(vTile.w + 0.5);
  ucTileRect = vTile.xyz;
  vec2 w = vWorld;
#ifdef UC_CELL_SAMPLING
  // stage-cell-centre sampling (09 §12, levelBy 'stage'): one sample per stage px, so u = 2 is
  // exactly the u = 1 picture ×2. The view is affine, so the derivatives are exact.
  vec2 o = ucCellCentreOffset();
  w += dFdx(vWorld) * o.x + dFdy(vWorld) * o.y;
#endif
  vec2 uv = (w - vTile.xy) / vTile.z;
  gl_FragColor = ucGrade(uv, w);
}
`;
}
